import { Plus, X } from 'lucide-react';

import type { CopyElement } from '../../api';
import { COPY_TYPE_OPTIONS, type TaxonomyOption } from '../../constants/taxonomy';

/**
 * The Copy / Dynamic Elements field: repeatable type + free-text rows.
 *
 * Purely controlled — rows in, rows out. "At least one row" is the consuming
 * form's requiredness rule, not this editor's: the drawer must also be able to
 * show an untouched empty state for a mixed selection.
 */

interface CopyElementsEditorProps {
  value: CopyElement[];
  onChange: (value: CopyElement[]) => void;
  isMixed?: boolean;
  readOnly?: boolean;
  /** The copy types offered; a carousel frame narrows them (no CTA). */
  typeOptions?: TaxonomyOption[];
}

export function CopyElementsEditor({
  value,
  onChange,
  isMixed,
  readOnly = false,
  typeOptions = COPY_TYPE_OPTIONS,
}: CopyElementsEditorProps) {
  const setRow = (index: number, patch: Partial<CopyElement>) => {
    if (readOnly) return;
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  if (readOnly && value.length === 0) {
    if (isMixed) {
      return (
        <p className="rounded-md border border-amber-200 bg-amber-50/40 px-2 py-1.5 text-[11.5px] italic text-slate-500">
          Mixed
        </p>
      );
    }
    return <p className="text-[12px] text-slate-400">—</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {isMixed && value.length === 0 && (
        <p className="rounded-md border border-amber-200 bg-amber-50/40 px-2 py-1.5 text-[11.5px] italic text-slate-500">
          Mixed — adding rows here replaces every selected asset’s copy
        </p>
      )}
      {value.map((row, index) => (
        // Index keys are safe here: rows have no identity beyond their position
        // and are only ever appended or removed through this editor.
        <div key={index} className="flex items-center gap-1.5">
          <select
            disabled={readOnly}
            value={row.type}
            onChange={(event) => setRow(index, { type: event.target.value })}
            className={`w-32 shrink-0 rounded-md border border-slate-200 px-1.5 py-1.5 text-[12px] text-slate-700 outline-none focus:border-[#0014cc] ${
              readOnly ? 'bg-slate-50/70 text-slate-600' : ''
            }`}
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            readOnly={readOnly}
            disabled={readOnly}
            value={row.value}
            onChange={(event) => setRow(index, { value: event.target.value })}
            placeholder="Text…"
            className={`min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-800 outline-none focus:border-[#0014cc] ${
              readOnly ? 'bg-slate-50/70 text-slate-600' : ''
            }`}
          />
          {!readOnly && (
            <button
              type="button"
              aria-label={`Remove row ${index + 1}`}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
              className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onChange([...value, { type: typeOptions[0].value, value: '' }])}
          className="flex w-fit items-center gap-1 rounded-md px-1.5 py-1 text-[11.5px] font-medium text-[#0014cc] hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2} />
          Add element
        </button>
      )}
    </div>
  );
}
